// The flow's one piece of state: the answers, the idempotency key for the
// submission they make, the availability answers already fetched, and the
// last submission's result. It lives in sessionStorage so a reload
// resumes where the URL says; every accessor tolerates a missing or
// throwing Storage, in which case the flow simply starts over.

import { z } from "zod";

import { emptyAnswers, type Answers } from "./answers";
import type { AvailabilityStatus } from "./flow";
import type { LeadResult } from "./submit";

export interface LeadFlowState {
  answers: Answers;
  /** Null until the contact step is first reached, and again after any
   * answer changes: a repeated submission of the same answers replays on
   * the server, an edited one is a new submission. */
  idempotencyKey: string | null;
  availability: Record<string, AvailabilityStatus>;
  outOfAreaAcknowledged: string[];
  /** The last submission's result. It carries what its screen needs, so it
   * outlives the answers it was made from. */
  result: LeadResult | null;
  /** The current answers are the ones `result` was made from, so a reload
   * starts a fresh form instead of resuming them. */
  answersSubmitted: boolean;
}

export type LeadFlowAction =
  | { type: "answer"; update: (answers: Answers) => Answers }
  | { type: "ensure_key"; key: string }
  | { type: "rotate_key" }
  | { type: "availability"; key: string; status: AvailabilityStatus }
  | { type: "acknowledge_out_of_area"; key: string }
  | { type: "submitted"; result: LeadResult }
  | { type: "status_read"; result: LeadResult }
  | { type: "reset" };

export function initialState(): LeadFlowState {
  return {
    answers: emptyAnswers(),
    idempotencyKey: null,
    availability: {},
    outOfAreaAcknowledged: [],
    result: null,
    answersSubmitted: false,
  };
}

export function leadFlowReducer(
  state: LeadFlowState,
  action: LeadFlowAction,
): LeadFlowState {
  switch (action.type) {
    case "answer": {
      const answers = action.update(state.answers);
      if (JSON.stringify(answers) === JSON.stringify(state.answers)) {
        return state;
      }
      return {
        ...state,
        answers,
        idempotencyKey: null,
        answersSubmitted: false,
      };
    }
    case "ensure_key":
      return state.idempotencyKey === null
        ? { ...state, idempotencyKey: action.key }
        : state;
    case "rotate_key":
      return { ...state, idempotencyKey: null };
    case "availability":
      return state.availability[action.key] === action.status
        ? state
        : {
            ...state,
            availability: {
              ...state.availability,
              [action.key]: action.status,
            },
          };
    case "acknowledge_out_of_area":
      return state.outOfAreaAcknowledged.includes(action.key)
        ? state
        : {
            ...state,
            outOfAreaAcknowledged: [...state.outOfAreaAcknowledged, action.key],
          };
    case "submitted":
      return { ...state, result: action.result, answersSubmitted: true };
    case "status_read":
      return { ...state, result: action.result };
    case "reset":
      return initialState();
  }
}

const STORAGE_KEY = "after.lead_form";

const text = z.string().catch("");
const flag = z.boolean().nullable().catch(null);
const name = { firstName: text, lastName: text };
const relation = z
  .object({
    type: z
      .enum([
        "spouse",
        "parent",
        "adult_child",
        "child",
        "grandparent",
        "power_of_attorney",
        "other",
      ])
      .nullable()
      .catch(null),
    otherText: text,
  })
  .catch({ type: null, otherText: "" });

const answersSchema = z
  .object({
    situation: z
      .enum(["death_occurred", "planning_ahead"])
      .nullable()
      .catch(null),
    decedent: z.object(name).catch({ firstName: "", lastName: "" }),
    where: z
      .object({
        type: z
          .enum([
            "hospital",
            "private_residence",
            "care_facility",
            "medical_examiner",
            "other",
          ])
          .nullable()
          .catch(null),
        zip: text,
      })
      .catch({ type: null, zip: "" }),
    relation,
    readyForCare: flag,
    preplanned: flag,
    preplanReference: text,
    planningFor: z.enum(["self", "loved_one", "both"]).nullable().catch(null),
    self: z
      .object({ ...name, dob: text, zip: text, hospice: flag })
      .catch(emptyAnswers().self),
    lovedOne: z
      .object({
        ...name,
        dob: text,
        zip: text,
        livesInMyZip: z.boolean().catch(true),
        hospice: flag,
        relation,
      })
      .catch(emptyAnswers().lovedOne),
    submitter: z
      .object({ ...name, email: text, phone: text })
      .catch(emptyAnswers().submitter),
  })
  .catch(emptyAnswers());

const confirmationStatus = z.enum([
  "awaiting_confirmation",
  "confirmed",
  "completed",
  "expired",
  "closed",
]);

const recordType = z.enum(["policy", "case"]);
const recordFor = z.enum(["self", "loved_one", "decedent"]);

const resultSchema = z.object({
  variant: z.enum(["case_received", "call_us", "already_received", "degraded"]),
  leadKind: z.enum(["death", "imminent", "planning"]),
  acknowledged: z.boolean(),
  submissionId: z.string().nullable(),
  eventId: z.string().nullable(),
  records: z.array(z.object({ type: recordType, for: recordFor })),
  quotes: z.array(
    z.object({
      type: recordType,
      for: recordFor,
      quoteId: z.string(),
      eventId: z.string().nullable(),
    }),
  ),
  status: confirmationStatus,
  landing: z
    .object({
      kind: z.enum(["quote", "case", "other"]),
      quoteId: z.string().nullable(),
      caseId: z.string().nullable(),
      eventId: z.string().nullable(),
    })
    .nullable()
    .catch(null),
  landingAnswers: answersSchema,
  verification: z
    .object({
      grant: z.string(),
      methods: z.array(z.enum(["sms_otp", "email_link"])).catch([]),
      phoneDisplay: z.string().nullable(),
      emailDisplay: z.string().nullable().catch(null),
    })
    .nullable(),
  caseId: z.string().nullable().catch(null),
});

const storedSchema = z.object({
  answers: answersSchema,
  idempotencyKey: z.string().nullable().catch(null),
  availability: z
    .record(z.string(), z.enum(["in_area", "out_of_area", "unknown"]))
    .catch({}),
  outOfAreaAcknowledged: z.array(z.string()).catch([]),
  result: resultSchema.nullable().catch(null),
  answersSubmitted: z.boolean().catch(false),
});

export function parseStoredState(raw: string | null): LeadFlowState | null {
  if (!raw) return null;
  try {
    const parsed = storedSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function loadState(): LeadFlowState {
  try {
    return (
      parseStoredState(window.sessionStorage.getItem(STORAGE_KEY)) ??
      initialState()
    );
  } catch {
    return initialState();
  }
}

/** The stored submission that still waits on the visitor's confirmation and
 * can be picked up from its id, if any. */
export function resultAwaitingConfirmation(
  state: LeadFlowState,
): LeadResult | null {
  const { result } = state;
  return result !== null &&
    result.status === "awaiting_confirmation" &&
    result.submissionId !== null
    ? result
    : null;
}

/** What survives a reload. A submission that has an id on the server keeps
 * its result, so its screen can be rebuilt and its status read live; the
 * answers it was made from are dropped, so the form starts fresh (the next
 * person on a shared device must not see them). A result with no id has
 * nothing to read back and is dropped too. */
export function persistableState(state: LeadFlowState): LeadFlowState {
  const { result } = state;
  const resumable = result !== null && result.submissionId !== null;
  if (!state.answersSubmitted && (result === null || resumable)) return state;
  return {
    ...(state.answersSubmitted ? initialState() : state),
    result: resumable ? result : null,
  };
}

export function saveState(state: LeadFlowState): void {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(persistableState(state)),
    );
  } catch {
    // Nothing to fall back to: the next change tries again.
  }
}

/** Drops the stored flow, once a confirmed submission has been placed on
 * the account. */
export function clearStoredState(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored that could be read back either.
  }
}
